export default class Utils {
    static range(size: number, startAt: number = 0): number[] {
        return [...Array(size).keys()].map((i) => i + startAt);
    }

    /**
     * Text colour for a background, decided like the backend (ColorConverter::getContrastForHexColor):
     * WCAG relative luminance, black above 0.179 – where black and white reach the same contrast ratio.
     */
    static contrastColor(color: string | null | undefined): '#000000' | '#FFFFFF' | null {
        const rgb = Utils.parseColor(color?.trim() ?? '');
        if (rgb === null) {
            return null;
        }

        const [r, g, b] = rgb.map((channel) => {
            const c = channel / 255;
            return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
        });

        return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.179 ? '#000000' : '#FFFFFF';
    }

    // Reads the formats the frontend design settings accept; transparency is blended with white, as in the backend.
    private static parseColor(color: string): number[] | null {
        // #rgb, #rrggbb or #rrggbbaa
        const hex = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i);
        if (hex) {
            const digits = hex[1].length === 3 ? hex[1].replace(/./g, '$&$&') : hex[1];
            const channels = [0, 2, 4].map((i) => parseInt(digits.slice(i, i + 2), 16));
            const alpha = digits.length === 8 ? parseInt(digits.slice(6, 8), 16) / 255 : 1;
            return Utils.blendWithWhite(channels, alpha);
        }

        // rgb(r, g, b) or rgba(r, g, b, a) with channels 0–255 and alpha 0–1
        const rgb = color.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*(\d*\.?\d+)\s*)?\)$/i);
        if (rgb) {
            const channels = rgb.slice(1, 4).map((channel) => Math.min(255, Number(channel)));
            return Utils.blendWithWhite(channels, rgb[4] === undefined ? 1 : Math.min(1, Number(rgb[4])));
        }

        return null;
    }

    private static blendWithWhite(channels: number[], alpha: number): number[] {
        return channels.map((channel) => Math.round(channel * alpha + 255 * (1 - alpha)));
    }
}
